import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const UPSELL_PROPERTY_KEY = '_cart_upsell';
const UPSELL_SOURCE = 'fluevog-cart-gift-card-upsell';

/**
 * Cart drawer gift card upsell: checkbox adds/removes the promo gift card.
 * Also removes the promo line when it is the only item left in the cart.
 *
 * Drawer content is refreshed in-place (not via full section morph) so the open
 * <dialog> keeps its modal top-layer.
 */
class FluevogCartGiftCardUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #busy = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #ensureTimer = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (this.dataset.needsCleanup === 'true') {
      this.#removePromo();
      return;
    }

    const checkbox = this.#checkbox;
    if (checkbox) {
      checkbox.addEventListener('change', this.#onCheckboxChange, { signal });
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#ensureTimer !== null) {
      clearTimeout(this.#ensureTimer);
      this.#ensureTimer = null;
    }
  }

  /** @returns {HTMLInputElement | null} */
  get #checkbox() {
    return this.querySelector('.fv-cart-gc-upsell__input');
  }

  get #variantId() {
    return this.dataset.variantId || '';
  }

  get #productHandle() {
    return this.dataset.productHandle || '';
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #lineKey() {
    return this.dataset.lineKey || '';
  }

  get #upsellProperty() {
    return this.dataset.upsellProperty || 'gift_card_20_for_10';
  }

  get #cartJsUrl() {
    const base = Theme.routes.cart_url || '/cart';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  get #cartAddJsUrl() {
    return Theme.routes.cart_add_url || '/cart/add.js';
  }

  get #cartChangeJsUrl() {
    const base = Theme.routes.cart_change_url || '/cart/change';
    return base.includes('.js') ? base : `${base}.js`;
  }

  #setBusy(isBusy) {
    this.#busy = isBusy;
    this.classList.toggle('is-busy', isBusy);
    const checkbox = this.#checkbox;
    if (checkbox) checkbox.disabled = isBusy;
  }

  #sectionIds() {
    const ids = new Set();
    if (this.#sectionId) ids.add(this.#sectionId);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  /** @param {object} item */
  #isPromoItem(item) {
    const handle = this.#productHandle;
    const variantId = Number(this.#variantId);
    if (handle && item.handle === handle) return true;
    if (variantId && Number(item.variant_id) === variantId) return true;
    const props = item.properties || {};
    return props[UPSELL_PROPERTY_KEY] === this.#upsellProperty;
  }

  async #fetchSections() {
    const sectionIds = this.#sectionIds();
    if (!sectionIds.length) return undefined;

    // Section Rendering API URL — avoid page-cache / bfcache stale HTML on mobile.
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    url.searchParams.set('_', String(Date.now()));

    return fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((r) => r.json());
  }

  /**
   * Update drawer markup without remounting the open <dialog>.
   * @param {Record<string, string> | undefined} sections
   * @returns {boolean}
   */
  #refreshDrawerFromSections(sections) {
    const sectionId = this.#sectionId;
    const html = sectionId && sections ? sections[sectionId] : null;
    if (!html) return false;

    const cartItems = document.querySelector('cart-items-component[data-drawer]');
    if (!(cartItems instanceof HTMLElement)) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('cart-items-component');
    if (!(next instanceof HTMLElement)) return false;

    const previousContent = cartItems.querySelector('.cart-drawer__content');
    const previousScrollTop = previousContent instanceof HTMLElement ? previousContent.scrollTop : 0;

    cartItems.innerHTML = next.innerHTML;
    if (typeof /** @type {any} */ (cartItems).updatedCallback === 'function') {
      /** @type {any} */ (cartItems).updatedCallback();
    }

    const nextContent = cartItems.querySelector('.cart-drawer__content');
    if (nextContent instanceof HTMLElement && previousScrollTop > 0) {
      nextContent.scrollTop = previousScrollTop;
    }

    return true;
  }

  /**
   * @param {object} cartLike
   * @param {Record<string, string> | undefined} [sections]
   */
  async #notifyCartUpdate(cartLike, sections) {
    let resolvedSections = sections;
    let refreshed = this.#refreshDrawerFromSections(resolvedSections);

    // Always fall back to a cache-busted section fetch when the POST payload
    // is missing sections or couldn't be applied (common on mobile Safari).
    if (!refreshed) {
      resolvedSections = await this.#fetchSections();
      refreshed = this.#refreshDrawerFromSections(resolvedSections);
    }

    const itemCount =
      typeof cartLike?.item_count === 'number'
        ? cartLike.item_count
        : (() => {
            const sectionHtml = resolvedSections?.[this.#sectionId];
            if (!sectionHtml) return 0;
            const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
            const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
            const parsed = countText ? parseInt(countText, 10) : NaN;
            return Number.isNaN(parsed) ? 0 : parsed;
          })();

    document.dispatchEvent(
      new CartUpdateEvent(cartLike || {}, UPSELL_SOURCE, {
        source: UPSELL_SOURCE,
        itemCount,
        // Omit sections so cart-items doesn't morph/refresh again (we already did).
        sections: undefined,
      })
    );

    return refreshed;
  }

  #onCheckboxChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || this.#busy) return;

    if (event.target.checked) {
      this.#addPromo();
    } else {
      this.#removePromo();
    }
  };

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (this.#busy) return;
    if (!(event instanceof CartUpdateEvent)) return;
    if (event.detail?.data?.source === UPSELL_SOURCE) return;

    if (this.#ensureTimer !== null) clearTimeout(this.#ensureTimer);
    this.#ensureTimer = setTimeout(() => {
      this.#ensureTimer = null;
      this.#ensureNotAlone();
    }, 500);
  };

  async #ensureNotAlone() {
    try {
      const cart = await fetch(this.#cartJsUrl, { cache: 'no-store' }).then((r) => r.json());
      if (!Array.isArray(cart.items) || cart.items.length === 0) return;

      const promoItems = cart.items.filter((item) => this.#isPromoItem(item));
      const otherItems = cart.items.filter((item) => !this.#isPromoItem(item));

      if (promoItems.length > 0 && otherItems.length === 0) {
        const key = promoItems[0].key || this.#lineKey;
        if (key) await this.#removePromo({ lineKey: key });
        return;
      }

      const overQty = promoItems.find((item) => Number(item.quantity) > 1);
      if (overQty?.key) {
        await this.#setPromoQuantity(overQty.key, 1);
      }
    } catch (error) {
      console.error('[fv-cart-gc-upsell] alone check failed', error);
    }
  }

  async #addPromo() {
    if (!this.#variantId) {
      const checkbox = this.#checkbox;
      if (checkbox) checkbox.checked = false;
      return;
    }

    this.#setBusy(true);

    try {
      const cart = await fetch(this.#cartJsUrl, { cache: 'no-store' }).then((r) => r.json());
      const existing = (cart.items || []).find((item) => this.#isPromoItem(item));

      if (existing) {
        if (Number(existing.quantity) > 1 && existing.key) {
          await this.#setPromoQuantity(existing.key, 1);
        } else {
          await this.#notifyCartUpdate(cart, undefined);
        }
        return;
      }

      const sectionIds = this.#sectionIds();
      const body = {
        items: [
          {
            id: Number(this.#variantId),
            quantity: 1,
            properties: {
              [UPSELL_PROPERTY_KEY]: this.#upsellProperty,
            },
          },
        ],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const response = await fetch(this.#cartAddJsUrl, fetchConfig('json', { body: JSON.stringify(body) })).then(
        (r) => r.json()
      );

      if (response.status) {
        const checkbox = this.#checkbox;
        if (checkbox) checkbox.checked = false;
        console.error('[fv-cart-gc-upsell] add failed', response.description || response.message);
        return;
      }

      const updatedCart = await fetch(this.#cartJsUrl, { cache: 'no-store' }).then((r) => r.json());
      const promoInCart = (updatedCart.items || []).some((item) => this.#isPromoItem(item));

      if (!promoInCart) {
        const checkbox = this.#checkbox;
        if (checkbox) checkbox.checked = false;
        console.error('[fv-cart-gc-upsell] add succeeded but promo missing from cart');
        return;
      }

      await this.#notifyCartUpdate(updatedCart, response.sections);
    } catch (error) {
      const checkbox = this.#checkbox;
      if (checkbox) checkbox.checked = false;
      console.error('[fv-cart-gc-upsell] add error', error);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   */
  async #setPromoQuantity(lineKey, quantity) {
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: this.#sectionIds().join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(this.#cartChangeJsUrl, fetchConfig('json', { body })).then((r) => r.json());

    if (response.errors) {
      console.error('[fv-cart-gc-upsell] qty cap failed', response.errors);
      return;
    }

    await this.#notifyCartUpdate(response, response.sections);
  }

  /**
   * @param {object} [options]
   * @param {string} [options.lineKey]
   */
  async #removePromo(options = {}) {
    this.#setBusy(true);

    try {
      let id = options.lineKey || this.#lineKey;

      if (!id) {
        const cart = await fetch(this.#cartJsUrl, { cache: 'no-store' }).then((r) => r.json());
        const match = (cart.items || []).find((item) => this.#isPromoItem(item));
        id = match?.key;
      }

      if (!id) {
        const checkbox = this.#checkbox;
        if (checkbox) checkbox.checked = false;
        return;
      }

      const body = JSON.stringify({
        id,
        quantity: 0,
        sections: this.#sectionIds().join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(this.#cartChangeJsUrl, fetchConfig('json', { body })).then((r) => r.json());

      if (response.errors) {
        const checkbox = this.#checkbox;
        if (checkbox) checkbox.checked = true;
        console.error('[fv-cart-gc-upsell] remove failed', response.errors);
        return;
      }

      await this.#notifyCartUpdate(response, response.sections);
    } catch (error) {
      const checkbox = this.#checkbox;
      if (checkbox) checkbox.checked = true;
      console.error('[fv-cart-gc-upsell] remove error', error);
    } finally {
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('fluevog-cart-gift-card-upsell')) {
  customElements.define('fluevog-cart-gift-card-upsell', FluevogCartGiftCardUpsell);
}
